// bytteguide_controller.js
//
// A small, reusable step-by-step question wizard ("bytteguide").
//
// The controller owns all state and flow/decision logic. Every piece of
// user-facing content lives in the markup and is toggled via targets and
// data attributes:
//   - Each question is a `question` target with a `data-key`.
//   - Each option `<button>` carries `data-key`, `data-value` and a
//     `data-recap` phrase, and triggers `bytteguide#answer` on click.
//   - Each summary fragment (`when`, `how`, `note`, `saftLink`) carries a
//     `data-value`; the controller reveals the fragment whose value matches
//     the outcome it computes from the answers.
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'progressText',
    'progressBar',
    'card',
    'question',
    'summary',
    'recap',
    'when',
    'how',
    'note',
    'noteBox',
    'systemLabel',
    'saftLink',
    'back',
    'restart',
  ]

  static values = {
    // Longest possible path (system → lønn → mva). Only used to fill the
    // progress bar; we intentionally don't show a "av N" denominator.
    maxQuestions: { type: Number, default: 3 },
  }

  connect() {
    this.reset()
  }

  reset() {
    this.answers = { system: null, lonn: null, mva: null, frekvens: null }
    this.recaps = {}
    this.systemLabel = ''
    this.history = []
    this.showQuestion('system')
  }

  restart() {
    this.reset()
  }

  // ---- Flow ----

  // Which question keys are on the active path given the current answers.
  activeKeys() {
    const keys = ['system', 'lonn']
    if (this.answers.lonn === 'nei') keys.push('mva')
    return keys
  }

  // First active question that hasn't been answered yet, or null when done.
  nextKey() {
    return this.activeKeys().find((key) => this.answers[key] === null) || null
  }

  answer(event) {
    const { key, value, recap, systemLabel } = event.currentTarget.dataset

    if (key === 'mva') {
      // Combined question: "nei" means no mva; any other value means mva is
      // reported at the chosen frequency ("toMnd" or "arlig").
      if (value === 'nei') {
        this.answers.mva = 'nei'
        this.answers.frekvens = null
      } else {
        this.answers.mva = 'ja'
        this.answers.frekvens = value
      }
    } else {
      this.answers[key] = value
    }

    this.recaps[key] = recap || ''
    if (key === 'system') this.systemLabel = systemLabel || ''
    this.history.push(key)

    // Dropping off the active path (e.g. lønn → "ja") clears the mva answer.
    if (!this.activeKeys().includes('mva')) {
      this.answers.mva = null
      this.answers.frekvens = null
      delete this.recaps.mva
    }

    const next = this.nextKey()
    if (next) this.showQuestion(next)
    else this.showSummary()
  }

  back() {
    if (this.history.length === 0) return
    const last = this.history.pop()
    this.answers[last] = null
    delete this.recaps[last]
    if (last === 'mva') {
      this.answers.frekvens = null // frekvens is derived from the mva answer
    } else if (last === 'lonn') {
      this.answers.mva = null // mva (and its frekvens) are downstream of lønn
      this.answers.frekvens = null
      delete this.recaps.mva
    }
    this.showQuestion(last)
  }

  // ---- Rendering ----

  showQuestion(key) {
    this.summaryTarget.hidden = true
    if (this.hasCardTarget) this.cardTarget.hidden = false
    this.questionTargets.forEach((el) => {
      el.hidden = el.dataset.key !== key
    })
    const number = this.activeKeys().indexOf(key) + 1
    this.progressTextTarget.textContent = `Spørsmål ${number}`
    this.setProgressBar(number)
    this.updateControls()
  }

  showSummary() {
    const { whenKey, howKey, noteKey } = this.decision()

    if (this.hasCardTarget) this.cardTarget.hidden = true
    this.reveal(this.whenTargets, whenKey)
    this.reveal(this.howTargets, howKey)
    this.reveal(this.noteTargets, noteKey)
    if (this.hasNoteBoxTarget) this.noteBoxTarget.hidden = !noteKey

    if (howKey === 'saft') {
      if (this.hasSystemLabelTarget) {
        this.systemLabelTarget.textContent = this.systemLabel || 'det gamle systemet'
      }
      this.reveal(this.saftLinkTargets, this.answers.system)
    }

    this.recapTarget.textContent = this.history
      .map((key) => this.recaps[key])
      .filter(Boolean)
      .join(' · ')

    this.summaryTarget.hidden = false
    this.progressTextTarget.textContent = 'Oppsummering'
    this.setProgressBar(this.maxQuestionsValue)
    this.updateControls()
  }

  // Map the answers to the "when" / "how" / "note" content keys. This is the
  // guide's decision logic; the matching content lives in the markup.
  decision() {
    const { system, lonn, mva, frekvens } = this.answers
    const isFiken = system === 'fiken'

    let whenKey
    if (lonn === 'ja') whenKey = 'maaned'
    else if (isFiken) whenKey = mva === 'nei' ? 'valgfriEnkel' : 'valgfri'
    else if (mva === 'nei') whenKey = 'fleksibelt'
    else if (frekvens === 'toMnd') whenKey = 'mvatermin'
    else whenKey = 'regnskapsaar'

    // Fiken keeps everything (incl. bilag) via "Komplett eksport"; every other
    // system uses a SAF-T import (regnskapsdata only). Tripletex has its own
    // wording, the rest share the generic SAF-T fragment.
    let howKey
    if (isFiken) howKey = 'komplett'
    else if (system === 'tripletex') howKey = 'saftTripletex'
    else howKey = 'saft'

    // Note advice: every SAF-T system gets the "keep booking in Luca" reminder;
    // Fiken without lønn gets its own note about not carrying on in Fiken.
    let noteKey = null
    if (!isFiken) noteKey = 'saft'
    else if (lonn === 'nei') noteKey = 'fiken'

    return { whenKey, howKey, noteKey }
  }

  // ---- Helpers ----

  // Show the single target whose data-value matches `value`, hide the rest.
  reveal(targets, value) {
    targets.forEach((el) => {
      el.hidden = el.dataset.value !== value
    })
  }

  setProgressBar(number) {
    const pct = Math.round((number / this.maxQuestionsValue) * 100)
    this.progressBarTarget.style.width = `${pct}%`
  }

  updateControls() {
    const show = this.history.length > 0
    if (this.hasBackTarget) this.backTarget.hidden = !show
    if (this.hasRestartTarget) this.restartTarget.hidden = !show
  }
}
